/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { status } from '@sportninja/common/constants/attendanceStatus'
import { t } from '@sportninja/common/i18n'
import {
  getPlayerTypesBySportId,
  getTeamOfficialTypesBySportId,
} from '@sportninja/common/selectors/types'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import Icon from 'src/components/Icon'
import { Container } from '../../components/Layout'
import PersonCell from '../../components/List/PersonCell'
import { Alert, Spin, Table } from 'antd'
import { playerSort } from 'src/utils/sortingUtils'
import { getTableStyles } from 'src/styles/tableStyles'
import useWindowSize from 'src/hooks/useWindowSize'

const renderNameColumn = ({ item }) => {
  let to
  if (item?.id) {
    to = `${ROUTES.PLAYER_ROOT}/${item.id}`
  }
  return (
    <PersonCell
      className='player-list-player'
      item={item}
      subTitle={item?.customer_identifier}
      as={to ? Link : undefined}
      to={to}
    />
  )
}

const renderAttendanceColumn = ({
  canViewAttendanceInfo,
  item,
  isGameStarted,
  isGenericGame,
  isGameCompleted = false,
}) => {
  // We still should display Starting Goalie, Suspended and Injuried info even
  // if the user does not have permission to view attendance info
  const isStartingGoalie =
    item?.player_type?.is_goalie === 1 && item?.is_starting && item?.is_playing
  if (isStartingGoalie) {
    return 'Starting Goalie'
  }
  if (item?.is_suspended && item?.suspension) {
    const suspension = item?.suspension
    const startDate = suspension?.start_date
      ? dayjs.utc(suspension.start_date).format('MMM D')
      : ''
    const endDate = suspension?.end_date
      ? dayjs.utc(suspension.end_date).format('MMM D')
      : 'Indefinitely'

    return (
      <div>
        <div
          css={css`
            text-transform: uppercase;
            color: ${colors.FIERY_RED};
            font-size: 14px;
          `}
        >
          Suspended
        </div>
        <div
          css={{
            marginTop: 2,
            fontSize: 13,
          }}
        >
          {startDate} - {endDate}
        </div>
      </div>
    )
  }
  if (item.is_injured) {
    return <div>{item.is_injured && <div>Injured</div>}</div>
  }
  if (isGameCompleted && isGameStarted && !isGenericGame) {
    return <div>{item.is_playing ? 'Playing' : 'Away'}</div>
  }
  if (isGameCompleted && isGameStarted && isGenericGame) {
    return <div>{item.is_playing ? 'Playing' : 'Away'}</div>
  }
  // From here, we only display attendance info if the user has permission to view it
  if (!canViewAttendanceInfo) {
    return ''
  }
  const attendanceString = getAttendanceString(item?.attendance_status)
  return <div>{attendanceString?.length > 0 ? attendanceString : '-'}</div>
}

const getAttendanceString = (statusNumber) => {
  switch (statusNumber) {
    case status.ATTENDING:
      return 'Attending'
    case status.MAYBE:
      return 'Maybe Attending'
    case status.NOT_ATTENDING:
      return 'Not Attending'
    default:
      return ''
  }
}

const GameRoster = ({
  isPublicRoute,
  gameId,
  isGameStarted,
  isGameCompleted = false,
  rosterId,
  isPlaceholder = false,
  isEmptyRoster = false,
  // Redux
  isInFrame,
  playerTypes,
  teamOfficialTypes,
  isGenericGame = false,
  canViewAttendanceInfo = false,
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [players, setPlayers] = useState([])
  const [officials, setOfficials] = useState([])

  const [sort, setSort] = useState('name_last')
  const [direction, setDirection] = useState('asc')

  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])

  useEffect(() => {
    const read = async () => {
      setLoading(true)
      try {
        const response = await req(`/games/${gameId}/rosters/${rosterId}`)
        setPlayers(response.data.players)
        setOfficials(response.data.team_officials)
      } catch (e) {
        console.error(e)
        setError(e?.message)
      } finally {
        setLoading(false)
      }
    }

    read()
  }, [isPublicRoute, gameId, rosterId])

  const regulars = [],
    affiliates = []

  players?.forEach((_player) => {
    const player = {
      ..._player,
      isGameStarted,
    }
    if (player.is_affiliate) {
      affiliates.push(player)
    } else {
      regulars.push(player)
    }
  })

  const sorter = playerSort(sort, direction)

  const handleTableChange = (pagination, filters, sorterInfo) => {
    if (sorterInfo.order) {
      const sortKey = sorterInfo.columnKey || sorterInfo.field
      const field =
        sortKey === 'name'
          ? 'name_last'
          : sortKey === 'position'
          ? 'player_type.id'
          : sortKey
      setSort(field)
      setDirection(sorterInfo.order === 'ascend' ? 'asc' : 'desc')
    } else {
      setSort('name_last')
      setDirection('asc')
    }
  }

  const playerColumns = useMemo(() => {
    const cols = [
      {
        title: t('common:name'),
        key: 'name',
        dataIndex: 'name_last',
        sorter: true,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder:
          sort === 'name_last'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => renderNameColumn({ item }),
      },
      {
        title: '#',
        key: 'player_number',
        dataIndex: 'player_number',
        sorter: true,
        showSorterTooltip: false,
        width: 60,
        sortOrder:
          sort === 'player_number'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (value) => value || '-',
      },
      {
        title: t('common:position'),
        key: 'position',
        dataIndex: ['player_type', 'id'],
        sorter: true,
        showSorterTooltip: false,
        width: 300,
        sortOrder:
          sort === 'player_type.id'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) =>
          playerTypes?.find(
            (type) => type.id === parseInt(item?.player_type?.id, 10)
          )?.name_full || '-',
      },
      !isInFrame && {
        title: 'DOB',
        key: 'birth_date',
        dataIndex: 'birth_date',
        sorter: false,
        showSorterTooltip: false,
        width: 300,
        render: (value) => (value ? dayjs.utc(value).format('YYYY-MM-DD') : ''),
      },
      {
        title: 'Status',
        key: 'status',
        sorter: false,
        showSorterTooltip: false,
        width: 300,
        render: (_, item) =>
          renderAttendanceColumn({
            canViewAttendanceInfo,
            item,
            isGameStarted,
            isGenericGame,
            isGameCompleted,
          }),
      },
    ].filter(Boolean)

    return cols
  }, [
    sort,
    direction,
    playerTypes,
    isInFrame,
    canViewAttendanceInfo,
    isGameStarted,
    isGenericGame,
    isGameCompleted,
  ])

  const officialColumns = useMemo(
    () => [
      {
        title: t('common:name'),
        key: 'name',
        sorter: false,
        showSorterTooltip: false,
        render: (_, item) => <PersonCell item={item} />,
      },
      {
        title: t('common:role'),
        key: 'team_official_type',
        dataIndex: 'team_official_type',
        sorter: false,
        showSorterTooltip: false,
        width: 350,
        render: (value) =>
          teamOfficialTypes?.find((type) => type.id === value)?.name_full ||
          '-',
      },
      {
        title: 'Attending',
        key: 'attending',
        sorter: false,
        showSorterTooltip: false,
        width: 350,
        render: (_, item) => {
          if (!canViewAttendanceInfo) {
            return ''
          }
          if (isGenericGame) {
            return '-'
          }
          if (isGameCompleted) {
            return item?.is_attending ? 'Attending' : 'Not Attending'
          }
          return '-'
        },
      },
    ],
    [teamOfficialTypes, canViewAttendanceInfo, isGenericGame, isGameCompleted]
  )

  if (isPlaceholder) {
    return (
      <Container
        noWrap
        column
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          margin: 0 8px;
          .list-container {
            flex: 1;
          }

          .list-inner {
            margin-left: 0;
            margin-right: 0;
          }
        `}
      >
        <Alert
          message='Informational Notes'
          description='Placeholder Teams do not have Rosters. They will be populated once the playoff schedule is set. Please check back later for updates.'
          type='info'
          showIcon
        />
      </Container>
    )
  }

  if (isEmptyRoster) {
    return (
      <Container
        noWrap
        column
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          margin: 0 8px;
          .list-container {
            flex: 1;
          }

          .list-inner {
            margin-left: 0;
            margin-right: 0;
          }
        `}
      >
        <Alert
          message='Informational Notes'
          description='Looks like this team has no players or officials rostered for this game. Please contact your league administrator for more information.'
          type='warning'
          showIcon
        />
      </Container>
    )
  }

  return (
    <Container
      noWrap
      column
      css={css`
        min-height: 100vh;
        width: 100%;
        margin: 0 8px 24px 8px;
        .list-container {
          flex: 1;
        }

        .list-inner {
          margin-left: 0;
          margin-right: 0;
        }
      `}
    >
      {error && !loading ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            gap: 16px;
          `}
        >
          <Icon
            name='exclamation-circle'
            css={css`
              height: 40px;
              width: 40px;
            `}
            color={colors.FIERY_RED}
            fontSize={32}
          />
          <p
            css={css`
              color: ${colors.WHITE};
              font-size: 16px;
              margin-top: 10px;
              text-align: center;
              text-decoration: underline;
            `}
          >
            {error}
          </p>
        </div>
      ) : (
        <>
          <p
            className='player-list-affiliates-title'
            css={css`
              font-weight: 700;
              color: ${isCanlan ? colors.CYAN_BLUE : colors.AMERICAN_YELLOW};
              text-transform: uppercase;
              margin-bottom: 16px;
            `}
          >
            Players
          </p>

          {loading && regulars.length === 0 ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 200px;
              `}
            >
              <Spin
                size='small'
                css={css`
                  .ant-spin-dot-item {
                    background-color: ${colors.PRIMARY};
                  }
                `}
              />
            </div>
          ) : (
            <Table
              columns={playerColumns}
              dataSource={regulars.sort(sorter)}
              rowKey='id'
              pagination={false}
              onChange={handleTableChange}
              size='small'
              scroll={{ x: isMobile ? 800 : undefined }}
              css={getTableStyles(isMobile)}
              locale={{ emptyText: 'No Players Found' }}
            />
          )}

          {affiliates.length > 0 && (
            <>
              <p
                className='player-list-affiliates-title'
                css={css`
                  margin-top: 24px;
                  font-weight: 700;
                  color: ${isCanlan
                    ? colors.CYAN_BLUE
                    : colors.AMERICAN_YELLOW};
                  text-transform: uppercase;
                  margin-bottom: 16px;
                `}
              >
                Affiliates
              </p>
              <Table
                columns={playerColumns}
                dataSource={affiliates.sort(sorter)}
                rowKey='id'
                pagination={false}
                size='small'
                scroll={{ x: isMobile ? 800 : undefined }}
                css={getTableStyles(isMobile)}
                locale={{
                  emptyText: t('common:affiliate_plural', { count: 2 }),
                }}
              />
            </>
          )}
          {officials.length > 0 && (
            <>
              <div
                css={css`
                  margin-top: 26px;
                  margin-bottom: 10px;
                  border-top: 1px solid rgba(255, 255, 255, 0.1);
                `}
              />
              <p
                className='player-list-affiliates-title'
                css={css`
                  margin-top: 24px;
                  font-weight: 700;
                  color: ${isCanlan
                    ? colors.CYAN_BLUE
                    : colors.AMERICAN_YELLOW};
                  text-transform: uppercase;
                  margin-bottom: 16px;
                `}
              >
                Team Officials
              </p>
              <Table
                columns={officialColumns}
                dataSource={officials.sort(sorter)}
                rowKey='id'
                pagination={false}
                size='small'
                scroll={{ x: isMobile ? 600 : undefined }}
                css={getTableStyles(isMobile)}
                locale={{
                  emptyText: t('common:official_plural', { count: 2 }),
                }}
              />
            </>
          )}
        </>
      )}
    </Container>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    isInFrame: state.auth.inFrame,
    playerTypes: getPlayerTypesBySportId(state, sportId),
    teamOfficialTypes: getTeamOfficialTypesBySportId(state, sportId),
  }
}

export default connect(mapStateToProps)(GameRoster)
